import { useTranslation } from "react-i18next";
import { CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from "recharts";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { BudgetForecast } from "@/lib/budget-planning";

type CurvePoint = BudgetForecast & { percent: number };

interface ForecastChartsProps {
  curve: CurvePoint[];
  percent: number;
  formatMoney: (value: number) => string;
}

function shortMoney(value: number): string {
  return value >= 1_000 ? `${Math.round(value / 1_000)}k` : `${Math.round(value)}`;
}

function ChartLegend({ items }: { items: Array<{ label: string; color: string }> }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
      {items.map(({ label, color }) => (
        <span
          key={label}
          className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground"
        >
          <span className="size-1.5 rounded-full" style={{ backgroundColor: color }} />
          {label}
        </span>
      ))}
    </div>
  );
}

function ChartMarkers({ percent }: { percent: number }) {
  return (
    <>
      <ReferenceLine x={100} stroke="var(--gray-300)" strokeDasharray="3 4" />
      <ReferenceLine x={percent} stroke="var(--gray-500)" strokeDasharray="3 4" />
    </>
  );
}

export function ForecastCharts({
  curve,
  percent,
  formatMoney,
}: ForecastChartsProps): React.ReactElement {
  const { t } = useTranslation("budgetPlanning");
  const seriesLabels: Record<string, string> = {
    totalSales: t("metricTotalSales"),
    totalProfit: t("metricTotalProfit"),
    totalSpend: t("metricTotalSpend"),
  };
  const businessConfig = {
    totalSales: { label: seriesLabels.totalSales, color: "var(--brand-9)" },
    totalProfit: { label: seriesLabels.totalProfit, color: "var(--green-11)" },
    totalSpend: { label: seriesLabels.totalSpend, color: "var(--amber-11)" },
  } satisfies ChartConfig;
  const businessLegend = [
    { label: t("metricTotalSales"), color: "var(--brand-9)" },
    { label: t("metricTotalProfit"), color: "var(--green-11)" },
    { label: t("metricTotalSpend"), color: "var(--amber-11)" },
  ];
  const tooltip = (
    <ChartTooltipContent
      indicator="line"
      labelFormatter={(_value, payload) =>
        t("chartPoint", { percent: payload[0]?.payload?.percent ?? "" })
      }
      formatter={(value, name, item) => (
        <>
          <span className="w-1 shrink-0 rounded-sm" style={{ backgroundColor: item.color }} />
          <span className="min-w-0 flex-1 text-muted-foreground">
            {seriesLabels[String(name)] ?? String(name)}
          </span>
          <span className="font-mono font-medium text-foreground tabular-nums">
            {formatMoney(Number(value))}
          </span>
        </>
      )}
    />
  );

  return (
    <section className="min-w-0 rounded-[10px] border border-line p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{t("businessChart")}</h3>
        <ChartLegend items={businessLegend} />
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">USD</p>
      <ChartContainer
        config={businessConfig}
        className="mt-1 h-[260px] w-full"
        role="img"
        aria-label={t("businessChart")}
      >
        <LineChart data={curve} margin={{ top: 8, right: 10, bottom: 0, left: -8 }}>
          <CartesianGrid vertical={false} stroke="var(--gray-200)" />
          <XAxis
            dataKey="percent"
            type="number"
            domain={[50, 200]}
            ticks={[50, 100, 150, 200]}
            tickFormatter={(value: number) => `${value}%`}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={shortMoney}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            width={48}
          />
          <ChartMarkers percent={percent} />
          <ChartTooltip content={tooltip} />
          <Line
            type="monotone"
            dataKey="totalSales"
            stroke="var(--brand-9)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="totalProfit"
            stroke="var(--green-11)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="totalSpend"
            stroke="var(--amber-11)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ChartContainer>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>{t("chartMarkers")}</span>
        <span>{t("chartAxis")}</span>
      </div>
    </section>
  );
}
